import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, MessageCircle, PenLine } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import api from "@/lib/api";

const formatDate = (value) => {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return new Intl.DateTimeFormat("hu-HU", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
};

export default function GuestbookPage() {
  const [guestbook, setGuestbook] = useState(null);
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setGuestbook(null);
    setError("");
    api.get("/guestbook", { params: { page } })
      .then((response) => {
        if (!cancelled) {
          setGuestbook(response.data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError("A vendégkönyv most nem tölthető be. Kérlek, próbáld meg később.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [page]);

  const changePage = (nextPage) => {
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="guestbook-page">
      <Navbar />
      <main className="max-w-4xl mx-auto px-5 sm:px-8 pt-32 pb-24">
        <div className="max-w-2xl">
          <span className="overline text-teal">üzenetek a műhelyből</span>
          <h1 className="font-serif text-5xl md:text-6xl font-medium text-ink mt-3">
            Vendégkönyv
          </h1>
          <p className="mt-5 text-base text-inkmuted leading-relaxed">
            Jólesik elolvasni, mit viszel magaddal egy közös alkalom után. Köszönöm,
            hogy itt hagyod a gondolataidat.
          </p>
          <Link
            to="/profil?tab=guestbook"
            data-testid="guestbook-write-link"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-poppy px-5 py-2.5 text-sm
              font-medium text-white hover:bg-poppy/90 transition-colors"
          >
            <PenLine size={16} strokeWidth={1.8} />
            Bejegyzést írok
          </Link>
        </div>

        <section className="mt-16" aria-label="Jóváhagyott vendégkönyv bejegyzések">
          {!guestbook && !error && (
            <p className="text-sm text-inkmuted" data-testid="guestbook-loading">
              Vendégkönyv betöltése…
            </p>
          )}
          {error && (
            <p className="text-sm text-poppy" data-testid="guestbook-error">
              {error}
            </p>
          )}
          {guestbook?.items?.length === 0 && (
            <div className="border-y border-ink/10 py-12 text-center" data-testid="guestbook-empty">
              <MessageCircle className="mx-auto text-teal" size={28} strokeWidth={1.5} />
              <p className="mt-4 text-sm text-inkmuted">Még nincs jóváhagyott bejegyzés.</p>
            </div>
          )}
          <div className="space-y-8">
            {guestbook?.items?.map((entry) => (
              <article
                key={entry.entry_id}
                className="border-l-2 border-teal/40 pl-6 sm:pl-8"
                data-testid="guestbook-entry"
              >
                <p className="font-serif text-2xl leading-relaxed text-ink whitespace-pre-wrap">
                  „{entry.content}”
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-inkmuted">
                  {entry.name && (
                    <span className="font-medium text-ink" data-testid="guestbook-entry-name">
                      {entry.name}
                    </span>
                  )}
                  <time dateTime={entry.created_at} data-testid="guestbook-entry-date">
                    {formatDate(entry.created_at)}
                  </time>
                </div>
              </article>
            ))}
          </div>

          {guestbook && guestbook.pages > 1 && (
            <nav
              className="mt-14 flex items-center justify-center gap-4"
              aria-label="Vendégkönyv oldalak"
              data-testid="guestbook-pagination"
            >
              <button
                type="button"
                disabled={page === 1}
                onClick={() => changePage(page - 1)}
                data-testid="guestbook-previous-page"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-ink/15
                  text-ink hover:bg-white transition-colors disabled:cursor-not-allowed disabled:opacity-35"
                aria-label="Előző oldal"
              >
                <ChevronLeft size={18} />
              </button>
              <span className="text-sm text-ink" data-testid="guestbook-page-label">
                {page}. oldal / {guestbook.pages}
              </span>
              <button
                type="button"
                disabled={page === guestbook.pages}
                onClick={() => changePage(page + 1)}
                data-testid="guestbook-next-page"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-ink/15
                  text-ink hover:bg-white transition-colors disabled:cursor-not-allowed disabled:opacity-35"
                aria-label="Következő oldal"
              >
                <ChevronRight size={18} />
              </button>
            </nav>
          )}
        </section>
      </main>
    </div>
  );
}